import { useEffect, useRef, useState, useCallback } from 'react';
import { clockApi } from '../services/api';

/**
 * Tracks the worker's GPS position via navigator.geolocation.watchPosition
 * while clocked in, batches pings and flushes to the backend every ~30s.
 *
 * Server-side filters jitter/inaccuracy and accumulates `total_km`, so this
 * hook simply forwards raw readings and surfaces the running total returned
 * by the backend.
 *
 * Usage:
 *   const { totalKm, status, error } = useShiftGpsTracker(isActive);
 */
export function useShiftGpsTracker(active) {
  const [totalKm, setTotalKm] = useState(0);
  const [status, setStatus] = useState('idle'); // idle | requesting | tracking | denied | unsupported | error
  const [error, setError] = useState(null);
  const [lastPingTs, setLastPingTs] = useState(null);

  const watchIdRef = useRef(null);
  const queueRef = useRef([]);
  const flushTimerRef = useRef(null);
  const inFlightRef = useRef(false);

  const flush = useCallback(async () => {
    if (inFlightRef.current) return;
    if (!queueRef.current.length) return;
    const batch = queueRef.current.splice(0, queueRef.current.length);
    inFlightRef.current = true;
    try {
      const { data } = await clockApi.gpsPing(batch);
      if (typeof data?.total_km === 'number') setTotalKm(data.total_km);
    } catch (e) {
      // Re-queue on failure (cap to avoid runaway memory)
      queueRef.current = [...batch.slice(-200), ...queueRef.current];
      // Only surface persistent errors after a few retries — silent for now
    } finally {
      inFlightRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!active) {
      // Stop tracking
      if (watchIdRef.current != null && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
      watchIdRef.current = null;
      if (flushTimerRef.current) clearInterval(flushTimerRef.current);
      flushTimerRef.current = null;
      // Final flush of anything queued
      flush();
      setStatus('idle');
      return;
    }

    if (!('geolocation' in navigator)) {
      setStatus('unsupported');
      setError('GPS not supported on this device.');
      return;
    }

    setStatus('requesting');
    setError(null);

    const handlePos = (pos) => {
      const ping = {
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy_m: pos.coords.accuracy,
        ts: new Date(pos.timestamp || Date.now()).toISOString(),
      };
      queueRef.current.push(ping);
      setLastPingTs(ping.ts);
      // Cap queue to avoid runaway memory
      if (queueRef.current.length > 500) {
        queueRef.current = queueRef.current.slice(-500);
      }
      setStatus('tracking');
    };

    const handleErr = (err) => {
      if (err.code === 1) {
        setStatus('denied');
        setError('Location permission denied.');
      } else {
        setStatus('error');
        setError(err.message || 'GPS error');
      }
    };

    watchIdRef.current = navigator.geolocation.watchPosition(handlePos, handleErr, {
      enableHighAccuracy: true,
      maximumAge: 5000,
      timeout: 30000,
    });

    // Flush every 30 seconds
    flushTimerRef.current = setInterval(() => { flush(); }, 30_000);

    return () => {
      if (watchIdRef.current != null && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
      watchIdRef.current = null;
      if (flushTimerRef.current) clearInterval(flushTimerRef.current);
      flushTimerRef.current = null;
      flush();
    };
  }, [active, flush]);

  return { totalKm, status, error, lastPingTs, flushNow: flush };
}
